import { QuestionCircleOutlined, SaveOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Spin,
  Switch,
  Tooltip,
  message
} from 'antd';
import * as moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import CustomAutocomplete from '../../components/CustomAutocomplete';
import CustomDatePicker from '../../components/CustomDatePicker';
import ResponsiveContainer from '../../components/ResponsiveContainer';
import InputMask from '../../components/input-mask';
import EmpresaContext from '../../context/empresa-context';
import UserContext from '../../context/user-context';
import {
  errorHandler,
  formatCelular,
  gruposSistema,
  gruposSistemaPorGrupo,
  parseCelular,
  prefijos3Digitos
} from '../../utils';
import { instanceRef } from '../../utils/axios';

const { Option } = Select;
const formItemLayout = {
  labelCol: {
    xs: { span: 24 }
  },
  wrapperCol: {
    xs: { span: 24 }
  }
};

const mapOptions = ({ id: value, descripcion: label }) => ({
  value,
  label
});

const fetchListSucursal = (searchText) => {
  return instanceRef.instance.get(`/sucursal/search`, {
    params: {
      current: 1,
      pageSize: 10,
      filters: {
        descripcion: searchText
      }
    }
  });
};

const UsuarioForm = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [gestion, setGestion] = useState(false);
  const { user, grupos } = useContext(UserContext);
  const [gruposPermitidos, setGruposPermitidos] = useState([]);
  const { empresa } = useContext(EmpresaContext);
  const [empresas, setEmpresas] = useState([]);
  const history = useHistory();
  const { id } = useParams();
  const [model, setModel] = useState();
  const [sucursalUsuario, setSucursalUsuario] = useState();
  const [hasError, setHasError] = useState(false);
  const [error, setError] = useState('');
  const [visible, setVisible] = useState(false);
  const [datosCambiados, setDatosCambiados] = useState({});
  const [datosCambiados2, setDatosCambiados2] = useState({});

  const onFinish = async ({ sucursal, ...values }) => {
    if (!hasError) {
      try {
        setLoading(true);
        let empresaId = values.empresa;
        if (user && user['cognito:groups'][0] === 'ADMIN') {
          empresaId = empresa.id;
        }
        const dataToSend = {
          ...values,
          username: values.usuario,
          email: values.correo,
          birthdate: values.birthdate.format('DD/MM/YYYY'),
          empresa: empresaId,
          celular: `+595${formatCelular(values.celular)}`
        };
        if (
          sucursal &&
          (values.grupo === 'ADMIN_SUCURSAL' || values.grupo === 'EJECUTIVO')
        ) {
          dataToSend.sucursal = sucursal.value;
        } else {
          dataToSend.sucursal = null;
        }
        if (!id) {
          await instanceRef.instance.post('auth/register', dataToSend);
          message.success('Usuario creado correctamente');
          history.push('/usuarios');
        } else {
          setDatosCambiados2({ ...dataToSend, id });
          showModal();
        }
      } catch (error2) {
        if (
          error2.response?.data?.message?.code === 'UsernameExistsException'
        ) {
          message.error('El usuario ya existe');
        } else {
          errorHandler(error2);
        }
      }
      setLoading(false);
    } else {
      message.error(error);
    }
  };

  const update = async () => {
    if (!hasError) {
      try {
        if (!(Object.entries(datosCambiados2).length === 0)) {
          setLoading(true);
          await instanceRef.instance.put(`auth/users/${id}`, datosCambiados2);
          setDatosCambiados2(null);
          history.push('/usuarios');
          message.success('Usuario actualizado correctamente');
        }
      } catch (error2) {
        history.push('/usuarios');
      }
      setLoading(false);
    } else {
      message.error(error);
    }
  };

  useEffect(() => {
    if (
      id &&
      !model &&
      empresas.length > 0 &&
      gruposPermitidos.length > 0 &&
      form
    ) {
      const loadModel = async () => {
        try {
          setLoading(true);
          const response = await instanceRef.instance.get(`/auth/users/${id}`);
          if (response.data?.data) {
            const usuario = response.data?.data;
            const modelo = {
              correo: usuario.correo,
              usuario: usuario.usuario,
              name: usuario.nombres,
              cedula: usuario.cedula,
              usuario_interno: usuario.usuario_interno,
              celular: parseCelular(usuario.celular),
              address: usuario.domicilio,
              birthdate: moment(usuario.fecha_nacimiento),
              grupo: usuario.grupo,
              empresa: usuario.empresa?.id,
              ...(usuario.sucursal && {
                sucursal: mapOptions(usuario.sucursal)
              }),
              gestion_sistema: usuario.gestion_sistema,
              gestion_usuarios: usuario.gestion_usuarios,
              externo: usuario.externo,
              matricula: usuario.matricula
            };

            setModel(modelo);
            form.setFieldsValue(modelo);
          }
        } catch (error2) {
          errorHandler(error2);
        }
        setLoading(false);
      };
      loadModel();
    }
    if (!id) {
      setModel({ gestion_sistema: true });
      form.setFieldsValue({ gestion_sistema: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, empresas, gruposPermitidos]);

  useEffect(() => {
    if (user && empresa) {
      const loadSucursal = async () => {
        try {
          setLoading(true);
          const response = await instanceRef.instance.get(
            `/auth/users/cognito/${user.sub}`
          );
          if (response.data?.data) {
            const usuario = response.data?.data;
            if (usuario.sucursal) {
              form.setFieldsValue({
                sucursal: {
                  value: usuario.sucursal.id,
                  label: usuario.sucursal.descripcion
                }
              });
              setSucursalUsuario(usuario.sucursal);
            }
          }
        } catch (error2) {
          errorHandler(error2);
        }
        setLoading(false);
      };
      loadSucursal();
    }
  }, [user, form, empresa]);

  useEffect(() => {
    if (user && grupos && empresa) {
      const grupo = user['cognito:groups'][0];
      const gruposPermitidosUsuario = grupos.filter((item) => {
        const grupoFiltro =
          gruposSistemaPorGrupo[grupo] &&
          gruposSistemaPorGrupo[grupo].includes(item.id);

        // Si la empresa no es Aseguradora/Empresa no mostrar la opción 'ADMIN_ASEGURADORA'
        if (item.id === 'ADMIN_ASEGURADORA' && !empresa.id_aseguradora) {
          return null;
        }

        return grupoFiltro;
      });
      setGruposPermitidos(gruposPermitidosUsuario);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, grupos, empresa]);

  useEffect(() => {
    if (empresa) {
      setEmpresas([empresa]);
    }
  }, [empresa]);

  useEffect(() => {
    if (empresas.length === 1) {
      form.setFieldsValue({ empresa: empresas[0].id });
    }
  }, [empresas, form]);

  useEffect(() => {
    if (gruposPermitidos.length === 1) {
      form.setFieldsValue({ grupo: gruposPermitidos[0].id });
    }
  }, [gruposPermitidos, form]);

  useEffect(() => {}, [error]);

  const verificarEmailUsuario = async (value, field) => {
    try {
      const empresaId = form.getFieldValue('empresa');
      const response = await instanceRef.instance.post('/auth/check', {
        params: { value, field, empresaId }
      });
      if (response.data.data) {
        let errorMessage = `Ya existe un usuario con éste Nombre/Código Interno de usuario ${value}`;
        if (field === 'correo') {
          errorMessage = `Ya existe un usuario con este correo ${value}`;
        }
        setHasError(true);
        setError(errorMessage);
        form.setFields([
          {
            name: field,
            errors: [errorMessage]
          }
        ]);
      } else {
        setHasError(false);
        setError('');
      }
    } catch (err) {
      // error
    }
  };

  const handleInputChange = async (field, value) => {
    try {
      if (field === 'Fecha de Nacimiento') {
        setDatosCambiados({ ...datosCambiados, [field]: value });
      } else if (field === 'Sucursal') {
        setDatosCambiados({ ...datosCambiados, [field]: value.label });
      } else if (field === 'Rol') {
        setDatosCambiados({ ...datosCambiados, [field]: gruposSistema[value] });
      } else {
        setDatosCambiados({ ...datosCambiados, [field]: value.target.value });
      }
    } catch (error2) {
      // error
    }
  };
  const handleInputChange2 = async (field, value) => {
    try {
      if (field === 'Fecha de Nacimiento') {
        setDatosCambiados2({ ...datosCambiados2, [field]: value });
      } else if (field === 'sucursal') {
        setDatosCambiados2({ ...datosCambiados2, [field]: value.value });
      } else if (field === 'grupo') {
        setDatosCambiados2({ ...datosCambiados2, [field]: value });
      } else if (field === 'celular') {
        setDatosCambiados2({
          ...datosCambiados2,
          [field]: `+595${formatCelular(value.target.value)}`
        });
      } else {
        setDatosCambiados2({
          ...datosCambiados2,
          [field]: value.target.value
        });
      }
    } catch (error2) {
      // error
    }
  };

  const showModal = () => {
    if (!(Object.entries(datosCambiados).length === 0)) {
      setVisible(true);
    } else {
      message.warning('No hay datos modificados');
    }
  };

  const handleOk = () => {
    setVisible(false);
    update();
  };

  const handleCancel = () => {
    setVisible(false);
  };

  useEffect(() => {
    if (user) {
      // Solo puede gestionar si tiene ambas banderas (gestion_usuarios y gestion_sistema)
      // No se puede editar a si mismo esas banderas
      // Puede editar a un nuevo usuario o a uno existente
      const puedeEditarGestion = async (idUsuarioEditando) => {
        const response = await instanceRef.instance.get(
          `/auth/users/cognito/${user.sub}`
        );
        if (response.data?.data) {
          const usuario = response.data?.data;
          if (
            Number(usuario.id) !== Number(idUsuarioEditando) ||
            !idUsuarioEditando
          ) {
            setGestion(usuario.gestion_sistema && usuario.gestion_usuarios);
          } else {
            setGestion(false);
          }
        }
      };
      puedeEditarGestion(id);
    }
  }, [user, id]);

  return (
    <ResponsiveContainer size="large">
      <Card
        title={`${!id ? 'Agregar' : 'Editar'} Usuario`}
        style={{ textAlign: 'left' }}
      >
        <Spin
          spinning={loading || !model}
          delay={500}
          size="large"
          tip="Cargando..."
        >
          <div style={{ maxWidth: '500px' }}>
            <Form
              {...formItemLayout}
              initialValues={model}
              scrollToFirstError
              form={form}
              name="usuario"
              autoComplete="off"
              layout="vertical"
              onFinish={onFinish}
            >
              <Form.Item
                label={
                  <span>
                    Correo&nbsp;
                    <Tooltip title="Se utilizará para validar el registro">
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </span>
                }
                name="correo"
                rules={[
                  {
                    required: true,
                    message: 'Correo es un campo requerido'
                  },
                  {
                    type: 'email',
                    message: 'El correo no es válido'
                  }
                ]}
              >
                {id ? (
                  <Input
                    placeholder="Se utilizará para validar el registro"
                    disabled
                    onChange={(value) => {
                      verificarEmailUsuario(value.target.value, 'correo');
                    }}
                  />
                ) : (
                  <Input
                    placeholder="Se utilizará para validar el registro"
                    readOnly={id}
                    onChange={(value) => {
                      verificarEmailUsuario(value.target.value, 'correo');
                    }}
                  />
                )}
              </Form.Item>
              <Form.Item
                label={
                  <span>
                    Nombre de Usuario para Acceso al Sistema&nbsp;
                    <Tooltip title="Se utilizará para inicio de sesión">
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </span>
                }
                rules={[
                  {
                    required: true,
                    message: 'Nombre de Usuario es un campo requerido',
                    whitespace: true
                  },
                  {
                    pattern: /^[a-z0-9._-]*$/u,
                    message:
                      'El Nombre de Usuario debe estar en minúscula y no debe contener espacios. Ej.: "joserodriguez”'
                  }
                ]}
                name="usuario"
                onChange={(e) => {
                  handleInputChange('Nombre de Usuario', e);
                  handleInputChange2('usuario', e);
                }}
              >
                {id ? (
                  <Input
                    placeholder="Se utilizará para inicio de sesión"
                    disabled
                    onChange={(value) => {
                      verificarEmailUsuario(value.target.value, 'usuario');
                    }}
                  />
                ) : (
                  <Input
                    placeholder="Se utilizará para inicio de sesión"
                    readOnly={id}
                    onChange={(value) => {
                      verificarEmailUsuario(value.target.value, 'usuario');
                    }}
                  />
                )}
              </Form.Item>
              <Form.Item
                label={
                  <span>
                    Nombre y apellido&nbsp;
                    <Tooltip title="Nombre a mostrar">
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </span>
                }
                rules={[
                  {
                    required: true,
                    message: 'Nombre y apellido es un campo requerido',
                    whitespace: true
                  }
                ]}
                name="name"
                onChange={(e) => {
                  handleInputChange('Nombre y apellido', e);
                  handleInputChange2('name', e);
                }}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label={
                  <span>
                    Nombre/Código Interno de Usuario&nbsp;
                    <Tooltip title="Nombre de usuario alternativo">
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </span>
                }
                rules={[
                  {
                    message:
                      'Nombre/Código Interno de Usuario es un campo requerido',
                    whitespace: true
                  }
                ]}
                name="usuario_interno"
                onChange={(e) => {
                  handleInputChange('Nombre/Código Interno', e);
                  handleInputChange2('usuario_interno', e);
                }}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Cédula de identidad"
                name="cedula"
                onChange={(e) => {
                  handleInputChange('Cédula', e);
                  handleInputChange2('cedula', e);
                }}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Fecha de Nacimiento"
                name="birthdate"
                rules={[{ required: true }]}
              >
                <CustomDatePicker
                  style={{ width: '100%' }}
                  placeholder="DD/MM/YYYY"
                  format="DD/MM/YYYY"
                  onChange={(momentDate, dateString) => {
                    handleInputChange('Fecha de Nacimiento', dateString);
                    handleInputChange2('birthdate', dateString);
                  }}
                />
              </Form.Item>

              <Form.Item
                label="Dirección"
                name="address"
                rules={[
                  {
                    required: true
                  }
                ]}
                onChange={(e) => {
                  handleInputChange('Dirección', e);
                  handleInputChange2('address', e);
                }}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Celular"
                name="celular"
                rules={[
                  {
                    required: true
                  },
                  {
                    pattern: `^\\((${prefijos3Digitos.join(
                      '|'
                    )})\\) [0-9]{3}-[0-9]{3}$`,
                    message: 'Número no válido'
                  }
                ]}
                onChange={(e) => {
                  handleInputChange('Celular', e);
                  handleInputChange2('celular', e);
                }}
              >
                <InputMask
                  mask="(999) 999-999"
                  placeholder="(___) ___-___"
                  addonBefore="+595"
                />
              </Form.Item>

              <Form.Item label="Rol" name="grupo" rules={[{ required: true }]}>
                <Select
                  showSearch
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  style={{ width: '100%' }}
                  optionFilterProp="children"
                  placeholder="Elegir un rol"
                  onChange={(e) => {
                    handleInputChange('Rol', e);
                    handleInputChange2('grupo', e);
                  }}
                >
                  {gruposPermitidos.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {gruposSistema[item.id]}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {user && user['cognito:groups'][0] === 'ADMIN' && (
                <Form.Item
                  label="Empresa"
                  name="empresa"
                  rules={[{ required: true }]}
                  onChange={(e) => {
                    handleInputChange('Empresa', e);
                    handleInputChange2('empresa', e);
                  }}
                >
                  <Select
                    showSearch
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    style={{ width: '100%' }}
                    optionFilterProp="children"
                    placeholder="Elegir una empresa"
                    disabled={empresas.length === 1}
                  >
                    {empresas.map((item) => (
                      <Option key={item.id} value={item.id}>
                        {item.nombre}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, curValues) =>
                  prevValues.grupo !== curValues.grupo
                }
              >
                {({ getFieldValue }) => {
                  if (
                    ['ADMIN_SUCURSAL', 'EJECUTIVO'].includes(
                      getFieldValue('grupo')
                    )
                  ) {
                    return (
                      <Form.Item
                        label="Sucursal"
                        name="sucursal"
                        rules={[{ required: true }]}
                      >
                        <CustomAutocomplete
                          placeholder="Buscar Sucursal"
                          fetchOptions={fetchListSucursal}
                          mapOptions={mapOptions}
                          disabled={sucursalUsuario}
                          onChange={(e) => {
                            handleInputChange('Sucursal', e);
                            handleInputChange2('sucursal', e);
                          }}
                        />
                      </Form.Item>
                    );
                  }
                  return null;
                }}
              </Form.Item>
              {user && (user['cognito:groups'][0] === 'ADMIN' || gestion) && (
                <>
                  <Form.Item
                    label="¿Puede gestionar la web?"
                    name="gestion_sistema"
                    valuePropName="checked"
                  >
                    <Switch
                      checkedChildren="Si"
                      unCheckedChildren="No"
                      onChange={(e) => {
                        handleInputChange('¿Puede gestionar la web?', {
                          target: { value: e }
                        });
                        handleInputChange2('gestion_usuarios', {
                          target: { value: e }
                        });
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    label="¿Puede gestionar usuarios?"
                    name="gestion_usuarios"
                    valuePropName="checked"
                  >
                    <Switch
                      checkedChildren="Si"
                      unCheckedChildren="No"
                      onChange={(e) => {
                        handleInputChange('¿Puede gestionar usuarios?', {
                          target: { value: e }
                        });
                        handleInputChange2('gestion_usuarios', {
                          target: { value: e }
                        });
                      }}
                    />
                  </Form.Item>
                </>
              )}
              <Form.Item
                label="¿Es un usuario externo?"
                name="externo"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="Si"
                  unCheckedChildren="No"
                  onChange={(e) => {
                    handleInputChange('¿Es un usuario externo?', {
                      target: { value: e }
                    });
                    handleInputChange2('externo', {
                      target: { value: e }
                    });
                  }}
                />
              </Form.Item>
              <Form.Item noStyle shouldUpdate>
                {form.getFieldValue('externo') === true && (
                  <Form.Item
                    label="Nro de matrícula"
                    name="matricula"
                    onChange={(e) => {
                      handleInputChange('Nro de matrícula', e);
                      handleInputChange2('matricula', e);
                    }}
                  >
                    <Input />
                  </Form.Item>
                )}
              </Form.Item>

              <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                {!id ? 'Registrar' : 'Actualizar'}
              </Button>
            </Form>
          </div>
        </Spin>
      </Card>
      <Modal
        title="Los datos fueron editados. ¿Está seguro de querer guardarlos?"
        open={visible}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <>
          {Object.keys(datosCambiados).map((key) => {
            let value;
            if (datosCambiados[key] === true) {
              value = 'Si';
            } else if (datosCambiados[key] === false) {
              value = 'No';
            } else {
              value = datosCambiados[key];
            }
            return (
              <Row key={key}>
                <Col span={8} align="start">
                  <p>{`${key}`}</p>
                </Col>
                <Col span={8} align="start">
                  <strong>{value}</strong>
                </Col>
              </Row>
            );
          })}
        </>
      </Modal>
    </ResponsiveContainer>
  );
};

export default UsuarioForm;
